/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import React, { useState, useEffect, useRef } from "react";
import useScrollMemory from "../hooks/useScrollMemory";
import TaxonomyStepper from "../components/TaxonomyStepper";
import BioIndexExploreNavigation from "../components/BioIndexExploreNavigation";
import { useParams, Link, useLocation } from "react-router-dom";
import { iconUrl, defaultImg } from "../utils/icons";
import { phylaByKingdom } from "../utils/taxonomy";
import { classesByPhylum } from "../utils/taxonomy";
import { ordersByClass } from "../utils/taxonomy";
import { familiesByOrder } from "../utils/taxonomy";
import { generaByFamily } from "../utils/taxonomy";
import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";

import "../styles/BioIndexCommunityMobile.css";
import "../styles/BioIndexExplore.css";
import ExplorePhotoModes from "../components/ExplorePhotoModes";

export const phylumImages = {
  Ochrophyta: "Sargassum.png",
  Bryophyta: "Racomitrium.png",
  Chordata: "Saguinus.png",
  Arthropoda: "Chinavia.png",
  Cnidaria: "Chrysaora.png",
  Echinodermata: "Paracentrotus.png",
  Mollusca: "Arion.png",
  Ginkgophyta: "Ginkgo.png",
  Basidiomycota: "Lactarius.png",
  Lycophyta: "Selaginella.png",
  Magnoliophyta: "Magnolia.png",
  Marchantiophyta: "Jungermannia.png",
  Cycadophyta: "Cycas.png",
  Pinophyta: "Pinus.png",
  Pteridophyta: "Nephrolepis.png",
  Ascomycota: "Cladonia.png",
};

function getPhylumImage(phylumName) {
  const filename = phylumImages[phylumName];
  return filename ? iconUrl(filename) : defaultImg;
}

function PhylumPage() {
  useScrollMemory();

  const { kingdomName, uid } = useParams();
  const location = useLocation();

  const currentUID = auth.currentUser?.uid;
  const dataUID = uid || currentUID;
  const phyla = phylaByKingdom[kingdomName] || [];

  const adminModeActive =
    !!uid && new URLSearchParams(location.search).get("adminMode") === "true";

  const adminModeSuffix = adminModeActive ? "?adminMode=true" : "";

  const [speciesCounts, setSpeciesCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  const [browseMode, setBrowseMode] = useState("taxonomy");
  const stepperPanelRef = useRef(null);
  const [stepperScroll, setStepperScroll] = useState({
    canScroll: false,
    thumbWidth: 72,
    thumbOffset: 0,
  });
  useEffect(() => {
    let cancelled = false;

    async function fetchSpeciesCounts() {
      setLoadingCounts(true);
      if (!dataUID) {
        setSpeciesCounts({});
        setLoadingCounts(false);
        return;
      }

      try {
        const snapshot = await getDocs(
          collection(db, "users", dataUID, "species"),
        );

        const counts = {};

        snapshot.forEach((doc) => {
          const data = doc.data();
          const genus = data?.genus?.toLowerCase();
          if (!genus) return;

          // 🔹 Family
          const familyKey = Object.keys(generaByFamily).find((fam) =>
            generaByFamily[fam].map((g) => g.toLowerCase()).includes(genus),
          );
          if (!familyKey) return;

          // 🔹 Order
          const orderKey = Object.keys(familiesByOrder).find((ord) =>
            familiesByOrder[ord]
              .map((f) => f.toLowerCase())
              .includes(familyKey.toLowerCase()),
          );
          if (!orderKey) return;

          // 🔹 Class
          const classKey = Object.keys(ordersByClass).find((cls) =>
            ordersByClass[cls]
              .map((o) => o.toLowerCase())
              .includes(orderKey.toLowerCase()),
          );
          if (!classKey) return;

          // 🔹 Phylum
          const phylumKey = Object.keys(classesByPhylum).find((phy) =>
            classesByPhylum[phy]
              .map((c) => c.toLowerCase())
              .includes(classKey.toLowerCase()),
          );
          if (!phylumKey) return;

          const normalizedPhylum = phylumKey.toLowerCase();

          counts[normalizedPhylum] = (counts[normalizedPhylum] || 0) + 1;
        });

        if (!cancelled) setSpeciesCounts(counts);
      } catch (err) {
        console.error("Failed to load species counts:", err);
        if (!cancelled) setSpeciesCounts({});
      } finally {
        if (!cancelled) setLoadingCounts(false);
      }
    }

    fetchSpeciesCounts();

    return () => {
      cancelled = true;
    };
  }, [dataUID]);

  // Match the subtle mobile stepper indicator to its actual scroll position.
  useEffect(() => {
    const viewport = stepperPanelRef.current?.querySelector(".overflow-x-auto");
    if (!viewport) return undefined;

    const trackWidth = 72;

    function updateStepperScroll() {
      const visibleWidth = viewport.clientWidth;
      const fullWidth = viewport.scrollWidth;
      const maxScroll = Math.max(0, fullWidth - visibleWidth);
      const canScroll = maxScroll > 2;
      const thumbWidth = canScroll
        ? Math.max(
            20,
            Math.min(trackWidth, (visibleWidth / fullWidth) * trackWidth),
          )
        : trackWidth;
      const fraction = canScroll
        ? Math.min(1, Math.max(0, viewport.scrollLeft / maxScroll))
        : 0;
      const thumbOffset = fraction * (trackWidth - thumbWidth);

      setStepperScroll((previous) => {
        if (
          previous.canScroll === canScroll &&
          Math.abs(previous.thumbWidth - thumbWidth) < 0.5 &&
          Math.abs(previous.thumbOffset - thumbOffset) < 0.5
        ) {
          return previous;
        }
        return { canScroll, thumbWidth, thumbOffset };
      });
    }

    viewport.addEventListener("scroll", updateStepperScroll, { passive: true });
    window.addEventListener("resize", updateStepperScroll);

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateStepperScroll)
        : null;
    observer?.observe(viewport);
    if (viewport.firstElementChild) {
      observer?.observe(viewport.firstElementChild);
    }

    updateStepperScroll();

    return () => {
      viewport.removeEventListener("scroll", updateStepperScroll);
      window.removeEventListener("resize", updateStepperScroll);
      observer?.disconnect();
    };
  }, []);

  // Totals refer only to the selected kingdom, not the user's entire collection.
  const visiblePhyla = phyla.filter(
    (phylum) => Number(speciesCounts[phylum.toLowerCase()] || 0) > 0,
  );
  const representedPhyla = visiblePhyla.length;

  const isOwnCollection = !uid || uid === currentUID;
  const modeLabels = {
    taxonomy: "Phyla",
    alpha: "Alphabetical browsing",
    date: "Observations by date",
    location: "Observations by location",
  };

  return (
    <div className="bioindex-explore-page bioindex-explore-phylum-page">
      <BioIndexExploreNavigation uid={uid} />

      <main className="bioindex-explore-main">
        {/* The summary is hidden on desktop by the shared V29 stylesheet. */}
        <header className="bioindex-explore-intro">
          <div className="bioindex-explore-intro-text">
            <span className="bioindex-explore-eyebrow">
              {isOwnCollection ? "Your collection" : "Visiting a collection"}
            </span>
            <h1>{modeLabels[browseMode]}</h1>
          </div>
        </header>

        <section
          ref={stepperPanelRef}
          className="bioindex-explore-stepper-panel"
          aria-label="Taxonomy navigation and browsing modes"
        >
          <TaxonomyStepper
            params={{ kingdomName }}
            currentLevel="phylum"
            uid={uid}
            browseMode={browseMode}
            setBrowseMode={setBrowseMode}
          />
          {stepperScroll.canScroll && (
            <div
              className="bioindex-explore-stepper-scroll-indicator sm:hidden"
              aria-hidden="true"
            >
              <div className="bioindex-explore-stepper-scroll-track">
                <span
                  className="bioindex-explore-stepper-scroll-thumb"
                  style={{
                    width: `${stepperScroll.thumbWidth}px`,
                    transform: `translateX(${stepperScroll.thumbOffset}px)`,
                  }}
                />
              </div>
            </div>
          )}
        </section>

        {browseMode === "taxonomy" ? (
          <section
            className="bioindex-explore-kingdoms"
            aria-label="Documented phyla"
          >
            {loadingCounts ? (
              <div className="bioindex-explore-empty" role="status">
                <span className="bioindex-explore-spinner" aria-hidden="true" />
                <h2>Loading phyla</h2>
              </div>
            ) : representedPhyla === 0 ? (
              <div className="bioindex-explore-empty">
                <h2>No phyla to explore yet</h2>
                <p>
                  This kingdom has no documented species in this collection.
                </p>
              </div>
            ) : (
              <div className="bioindex-explore-kingdom-grid">
                {visiblePhyla.map((phylum) => {
                  const key = phylum.toLowerCase();
                  const count = speciesCounts[key] || 0;
                  const to = uid
                    ? `/explore/${uid}/kingdom/${kingdomName}/phylum/${key}${adminModeSuffix}`
                    : `/kingdom/${kingdomName}/phylum/${key}`;

                  return (
                    <Link
                      key={phylum}
                      to={to}
                      className="bioindex-explore-kingdom-card bioindex-explore-phylum-card"
                    >
                      <span
                        className="bioindex-explore-kingdom-count"
                        aria-label={`${count.toLocaleString()} species`}
                        title={`${count.toLocaleString()} species`}
                      >
                        {count.toLocaleString()}
                      </span>
                      <div className="bioindex-explore-kingdom-illustration">
                        <img
                          src={getPhylumImage(phylum)}
                          alt=""
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = defaultImg;
                          }}
                        />
                      </div>
                      <div className="bioindex-explore-kingdom-caption">
                        <h2>{phylum}</h2>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        ) : (
          <section
            className="bioindex-explore-modes-panel"
            aria-label={modeLabels[browseMode]}
          >
            <ExplorePhotoModes
              mode={browseMode}
              dataUID={dataUID}
              routeUID={uid}
              isAdminCollectionMode={adminModeActive}
            />
          </section>
        )}
      </main>

      <footer className="bioindex-explore-footer">
        <p>© {new Date().getFullYear()} BioIndex™. All rights reserved.</p>
        <nav aria-label="Legal and contact links">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/community-guidelines">Guidelines</Link>
          <Link to="/copyright">Copyright</Link>
          <Link to="/contact">Contact</Link>
        </nav>
      </footer>
    </div>
  );
}

export default PhylumPage;
